// frontend/src/widgets/JobProgressWidget.jsx
// Project:      Vision ANPR by Castle Traffic Ltd - Automatic Number Plate Recognition
// Author:       Leon du Plooy - Castle Traffic Ltd - Copyright 2026
// Contact:      dev@castle-traffic.co.uk
// Created:      2025-07-17
// Updated:      2026-10-07 15:18 BST
//
// Description:
//   Widget for tracking ANPR job progress in real time.
//
// Lifecycle Status:
//   Active.
//
// Change Summary (2026-08-27):
//   - Removed all automatic calls to /api/system/status.
//   - Removed GPU/system diagnostics from the job-progress lifecycle.
//   - Preserved job-status polling at 2-second intervals.
//   - Added safer handling when jobId is missing.
//   - Added request cancellation on unmount/job change.
//   - Prevented overlapping job-status requests.
//
// Mobile Responsiveness (2026-09-08):
//   - Preserves the existing centred vertical progress presentation.
//   - Uses the same single render path on desktop and mobile.
//   - Keeps the circular progress gauge centred and readable on narrow screens.
//   - Allows long job references, current-step text and errors to wrap safely.
//   - Uses compact mobile padding while preserving the desktop widget fill behaviour.
//   - Expands the nearest fixed-height mobile widget host when required so content
//     cannot be clipped by a desktop-derived ReactGridLayout/mobile host height.
//   - No chevrons or alternate mobile progress model are introduced.
//
// Security / API Alignment (2026-09-08):
//   - Replaced direct axios usage and manual localStorage token handling with the
//     canonical axiosInstance authentication path.
//   - The widget supplies no tenant context and does not construct Authorization
//     headers itself.
//
// Operational Notes:
//   - This widget is responsible only for job progress.
//   - Server CPU/GPU diagnostics belong in explicit administrative diagnostic widgets.
//   - Mounting this widget must generate zero /api/system/status requests.
//   - Job status polling remains at 2-second intervals.
//   - Polling remains guarded against overlapping requests and is cancelled when
//     the widget unmounts or the selected job changes.
//
// Future Intent:
//   - Replace fixed polling with event-driven progress updates when the job runtime
//     exposes a suitable push/subscription mechanism.

import React, {
  useCallback,
  useEffect,
  useLayoutEffect,
  useMemo,
  useRef,
  useState,
} from "react";

import axiosInstance from "../api/axiosInstance";
import { useTheme } from "../context/ThemeContext.jsx";

const POLL_INTERVAL_MS = 2000;
const MOBILE_MEDIA_QUERY = "(max-width: 767px)";

const JobProgressWidget = ({ jobId, jobRef }) => {
  const { theme } = useTheme();

  const widgetRootRef = useRef(null);
  const requestInFlightRef = useRef(false);

  const [status, setStatus] = useState("pending");
  const [progress, setProgress] = useState(0);
  const [currentStep, setCurrentStep] = useState("Starting...");
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [workPackages, setWorkPackages] = useState([]);
  const [workPackageWorkflows, setWorkPackageWorkflows] = useState({});

  const loadWorkPackageProgress = useCallback(async () => {
    if (!jobId) return;
    try {
      const { data } = await axiosInstance.get(`/jobs/${jobId}/work-packages`, { baseURL: "/api" });
      const packages = data || [];
      setWorkPackages(packages);
      const settled = await Promise.allSettled(packages.map(async (wp) => {
        const res = await axiosInstance.get(`/jobs/${jobId}/work-packages/${wp.id}/workflow`, { baseURL: "/api" });
        return [wp.id, res.data];
      }));
      const map = {}; settled.forEach((result) => { if (result.status === "fulfilled") map[result.value[0]] = result.value[1]; });
      setWorkPackageWorkflows(map);
    } catch {
      setWorkPackages([]); setWorkPackageWorkflows({});
    }
  }, [jobId]);

  useEffect(() => { loadWorkPackageProgress(); const r=()=>loadWorkPackageProgress(); window.addEventListener("vision:tasks-changed",r); return()=>window.removeEventListener("vision:tasks-changed",r); }, [loadWorkPackageProgress]);


  const fetchJobStatus = useCallback(
    async (signal) => {
      if (!jobId || requestInFlightRef.current) {
        return;
      }

      requestInFlightRef.current = true;

      try {
        const response = await axiosInstance.get(
          `/jobs/${jobId}/status`,
          {
            signal,
          }
        );

        const nextStatus =
          response?.data?.status ||
          "pending";

        const rawProgress =
          Number(response?.data?.progress);

        const nextProgress =
          Number.isFinite(rawProgress)
            ? Math.min(
                100,
                Math.max(0, rawProgress)
              )
            : 0;

        setStatus(nextStatus);
        setProgress(nextProgress);
        setCurrentStep(
          response?.data?.current_step ||
            "Processing..."
        );
        setError("");
      } catch (requestError) {
        if (
          requestError?.code === "ERR_CANCELED" ||
          requestError?.name === "CanceledError"
        ) {
          return;
        }

        console.error(
          "[JobProgressWidget] Failed to fetch job status:",
          requestError
        );

        setError(
          requestError?.response?.data?.detail ||
            requestError?.message ||
            "Unable to retrieve job status."
        );
      } finally {
        requestInFlightRef.current = false;
        setLoading(false);
      }
    },
    [jobId]
  );

  useEffect(() => {
    if (!jobId) {
      setLoading(false);
      setError("No job has been selected.");
      return undefined;
    }

    setLoading(true);
    setError("");

    const controller =
      new AbortController();

    fetchJobStatus(controller.signal);

    const interval = window.setInterval(
      () => {
        fetchJobStatus(
          controller.signal
        );
      },
      POLL_INTERVAL_MS
    );

    return () => {
      window.clearInterval(interval);
      controller.abort();
      requestInFlightRef.current = false;
    };
  }, [
    fetchJobStatus,
    jobId,
  ]);

  const getArcColor = () => {
    if (status === "failed") {
      return "#dc2626";
    }

    if (progress < 50) {
      return "#f59e0b";
    }

    return "#16a34a";
  };

  const getStatusColor = () => {
    switch (status) {
      case "completed":
        return "#16a34a";

      case "processing":
        return "#f59e0b";

      case "failed":
        return "#dc2626";

      default:
        return "#6b7280";
    }
  };

  const arcColor =
    getArcColor();

  const arcGradient =
    `conic-gradient(${arcColor} ${
      progress * 3.6
    }deg, #e5e7eb ${
      progress * 3.6
    }deg)`;

  const displayJobRef = useMemo(
    () =>
      jobRef ||
      (jobId
        ? `Job ${jobId}`
        : "No job selected"),
    [
      jobId,
      jobRef,
    ]
  );

  /*
   * Mobile page layouts can wrap a widget in a desktop-derived fixed-height host.
   * Keep this component reusable by finding that host locally rather than relying
   * on JobsPage or any other parent page to pass responsive state.
   */
  useLayoutEffect(() => {
    if (
      typeof window === "undefined" ||
      !window.matchMedia
    ) {
      return undefined;
    }

    const root = widgetRootRef.current;

    if (!root) {
      return undefined;
    }

    const mediaQuery =
      window.matchMedia(
        MOBILE_MEDIA_QUERY
      );

    let animationFrame = 0;
    let hostRecord = null;

    const restoreHost = () => {
      if (!hostRecord?.node) {
        return;
      }

      hostRecord.node.style.height =
        hostRecord.height;
      hostRecord.node.style.minHeight =
        hostRecord.minHeight;

      hostRecord = null;
    };

    const findFixedMobileHost = () => {
      const rootRect =
        root.getBoundingClientRect();

      const candidates = [];

      let node =
        root.parentElement;

      let depth = 0;

      while (
        node &&
        node !== document.body &&
        depth < 6
      ) {
        const rect =
          node.getBoundingClientRect();

        const inlineHeight =
          String(
            node.style?.height ||
              ""
          ).trim();

        const widthMatches =
          Math.abs(
            rect.width -
              rootRect.width
          ) < 90;

        const hasFixedInlineHeight =
          inlineHeight &&
          inlineHeight !== "auto" &&
          inlineHeight !== "100%" &&
          inlineHeight !== "100vh";

        if (
          widthMatches &&
          hasFixedInlineHeight
        ) {
          candidates.push(node);
        }

        node =
          node.parentElement;

        depth += 1;
      }

      return candidates.length
        ? candidates[0]
        : null;
    };

    const applyMobileSizing = () => {
      cancelAnimationFrame(
        animationFrame
      );

      restoreHost();

      root.style.height = "";
      root.style.minHeight = "";

      if (!mediaQuery.matches) {
        return;
      }

      animationFrame =
        requestAnimationFrame(
          () => {
            root.style.height =
              "auto";
            root.style.minHeight =
              "0px";

            animationFrame =
              requestAnimationFrame(
                () => {
                  const host =
                    findFixedMobileHost();

                  if (!host) {
                    return;
                  }

                  hostRecord = {
                    node: host,
                    height:
                      host.style.height,
                    minHeight:
                      host.style.minHeight,
                  };

                  const hostRect =
                    host.getBoundingClientRect();

                  const rootRect =
                    root.getBoundingClientRect();

                  const wrapperChromeHeight =
                    Math.max(
                      0,
                      rootRect.top -
                        hostRect.top
                    );

                  const requiredHostHeight =
                    Math.ceil(
                      Math.max(
                        host.scrollHeight,
                        wrapperChromeHeight +
                          rootRect.height +
                          8
                      )
                    );

                  const currentHeight =
                    Math.ceil(
                      hostRect.height
                    );

                  if (
                    requiredHostHeight >
                    currentHeight
                  ) {
                    host.style.height =
                      `${requiredHostHeight}px`;

                    host.style.minHeight =
                      `${requiredHostHeight}px`;
                  }
                }
              );
          }
        );
    };

    applyMobileSizing();

    window.addEventListener(
      "resize",
      applyMobileSizing
    );

    const handleMediaChange = () =>
      applyMobileSizing();

    if (
      mediaQuery.addEventListener
    ) {
      mediaQuery.addEventListener(
        "change",
        handleMediaChange
      );
    } else {
      mediaQuery.addListener(
        handleMediaChange
      );
    }

    return () => {
      cancelAnimationFrame(
        animationFrame
      );

      window.removeEventListener(
        "resize",
        applyMobileSizing
      );

      if (
        mediaQuery.removeEventListener
      ) {
        mediaQuery.removeEventListener(
          "change",
          handleMediaChange
        );
      } else {
        mediaQuery.removeListener(
          handleMediaChange
        );
      }

      restoreHost();

      root.style.height = "";
      root.style.minHeight = "";
    };
  }, [
    currentStep,
    displayJobRef,
    error,
    jobId,
    loading,
    progress,
    status,
  ]);

  if (!jobId) {
    return (
      <div
        ref={widgetRootRef}
        className="h-auto min-h-0 w-full p-3 md:h-full md:p-4 flex flex-col font-sans"
        style={{
          minHeight: "220px",
          alignItems: "center",
          justifyContent: "center",
          textAlign: "center",
          boxSizing: "border-box",
        }}
      >
        <div
          style={{
            color: "#6b7280",
            fontSize: "0.9rem",
            maxWidth: "100%",
            overflowWrap: "anywhere",
          }}
        >
          No job has been selected for progress monitoring.
        </div>
      </div>
    );
  }

  return (
    <div
      ref={widgetRootRef}
      className="h-auto min-h-0 w-full p-3 md:h-full md:p-4 flex flex-col gap-3 md:gap-4 font-sans"
      style={{
        minHeight: "260px",
        justifyContent: "space-between",
        boxSizing: "border-box",
      }}
    >
      {loading ? (
        <div
          className="flex min-h-[220px] w-full items-center justify-center text-center text-sm text-gray-600"
          aria-live="polite"
        >
          Loading job status...
        </div>
      ) : (
        <>
          <h3 className="m-0 text-center text-lg font-semibold text-gray-800">
            Job Status
          </h3>

          <p className="m-0 text-center text-base leading-6">
            Currently Running:{" "}
            <span
              className="font-bold break-words"
              style={{
                color:
                  theme?.accent_color ||
                  "#0d6efd",
                overflowWrap:
                  "anywhere",
              }}
            >
              {displayJobRef}
            </span>
          </p>

          <div className="flex w-full justify-center">
            <span
              className="max-w-full rounded-full px-4 py-2 text-center text-sm font-bold uppercase text-white break-words"
              style={{
                backgroundColor:
                  getStatusColor(),
                overflowWrap:
                  "anywhere",
              }}
            >
              {String(
                status ||
                  "pending"
              ).toUpperCase()}
            </span>
          </div>

          <div
            className="relative mx-auto h-28 w-28 shrink-0"
            style={{
              background:
                arcGradient,
              borderRadius:
                "50%",
              padding:
                "6px",
            }}
            role="progressbar"
            aria-label="Job progress"
            aria-valuemin={0}
            aria-valuemax={100}
            aria-valuenow={progress}
          >
            <div
              className="absolute inset-0 flex items-center justify-center rounded-full bg-white"
              style={{
                margin:
                  "6px",
              }}
            >
              <span className="text-2xl font-bold">
                {progress}%
              </span>
            </div>
          </div>

          <div className="w-full text-center">
            <p className="m-0 font-bold">
              Current Step:
            </p>

            <p
              className="mt-1 mb-0 text-base leading-6 text-gray-600 break-words"
              style={{
                overflowWrap:
                  "anywhere",
              }}
            >
              {currentStep}
            </p>
          </div>

          {error && (
            <div
              role="alert"
              className="w-full break-words text-center"
              style={{
                marginTop:
                  "4px",
                color:
                  "#b91c1c",
                fontSize:
                  "0.85rem",
                overflowWrap:
                  "anywhere",
              }}
            >
              {error}
            </div>
          )}

          <div
            className="w-full text-center"
            style={{
              marginTop:
                "4px",
              color:
                "#6b7280",
              fontSize:
                "0.75rem",
            }}
          >
            Job status refreshes every 2 seconds.
          </div>

          {workPackages.length > 0 && (
            <div className="w-full mt-4 space-y-3">
              <div className="font-semibold text-left">Work Package Progress</div>
              {workPackages.map((wp) => {
                const wf = workPackageWorkflows[wp.id];
                const tasks = wf ? (wf.steps || []).flatMap((step) => step.tasks || []) : [];
                return (
                  <div key={wp.id} className="rounded-lg border border-gray-200 p-3 text-left">
                    <div className="flex items-center justify-between gap-2">
                      <div className="font-medium">{wp.title}</div>
                      <div className="text-sm">{wf ? `${Math.round(Number(wf.progress_pct) || 0)}%` : "No workflow"}</div>
                    </div>
                    {wf && <div className="mt-2 space-y-1">{tasks.map((task) => (
                      <button type="button" key={task.id} onClick={() => window.dispatchEvent(new CustomEvent("vision:open-task", { detail: { jobId: Number(jobId), workPackageId: wp.id, taskId: task.id } }))} className="w-full min-h-10 flex items-center justify-between gap-3 rounded px-2 hover:bg-gray-50 text-sm">
                        <span className="truncate">{task.status === "completed" ? "✓" : task.status === "ready" ? "●" : "○"} {task.title}</span>
                        <span className="text-xs capitalize shrink-0">{String(task.status).replace(/_/g, " ")}</span>
                      </button>
                    ))}</div>}
                  </div>
                );
              })}
            </div>
          )}
        </>
      )}
    </div>
  );
};

export default JobProgressWidget;
